import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useLazyGetActiveImportQuery } from 'store/services/import/importApi';
import { importJobStarted, importProgress } from 'store/slices/importSlice';

/**
 * Checks for active imports on app initialization.
 * Rehydrates Redux import state if an active job is found,
 * so the top nav progress pill reappears after page refresh.
 *
 * Call this once in SocketProvider for authenticated users.
 */
export const useImportRecovery = (enabled: boolean) => {
  const dispatch = useDispatch();
  const hasCheckedRef = useRef(false);
  const currentJobId = useSelector((state: RootState) => state.import.job.jobId);
  const [fetchActiveImport] = useLazyGetActiveImportQuery();

  useEffect(() => {
    // Skip if not enabled, already checked, or already tracking a job
    if (!enabled || hasCheckedRef.current || currentJobId) return;
    hasCheckedRef.current = true;

    const checkActiveImport = async () => {
      try {
        const result = await fetchActiveImport().unwrap();
        if (result.hasActiveImport && result.import) {
          const activeJob = result.import;

          // Determine target list from backend response, default to 'have'
          const targetList = (activeJob as any).targetList === 'want' ? 'want' : 'have';

          // Rehydrate Redux state
          dispatch(importJobStarted({
            jobId: activeJob.jobId,
            totalRows: activeJob.totalRows,
            targetList,
          }));

          // If partially complete, update progress
          if (activeJob.processedRows > 0) {
            dispatch(importProgress({
              jobId: activeJob.jobId,
              processedRows: activeJob.processedRows,
              totalRows: activeJob.totalRows,
              matched: activeJob.matched,
              skipped: activeJob.skipped,
              failed: activeJob.failed,
            }));
          }
        }
      } catch (err) {
        console.error('[ImportRecovery] Failed to check active imports:', err);
      }
    };

    checkActiveImport();
  }, [enabled, currentJobId, dispatch, fetchActiveImport]);
};

export default useImportRecovery;
